import { Injectable } from '@angular/core';
import _ from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MakersiteService } from '@services/makersite.service';
import { EventDelegatorService } from '@services/event-delegator.service';
import { LocalStorageService } from '@services/local-storage.service';
import { Router } from '@angular/router';
import { ToastrService } from '@services/toaster.service';
import { GlobalDataService } from '@services/global-data.service';
import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';
import { UnauthorizedErrorDialog } from '@dialogs/unauthorized-error/unauthorized-error-dialog.component';
import { InviteShareComponent } from '@dialogs/share-invite/share-invite-dialog.component';
import {
  InlineFormSchemaDialogComponent
} from '@dialogs/inline-form-schema-dialog/inline-form-schema-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class LayersService {
  private analyzeData: BehaviorSubject<any> = new BehaviorSubject(undefined);
  private pins: BehaviorSubject<any> = new BehaviorSubject([]);
  layersEvents: BehaviorSubject<any> = new BehaviorSubject(null);
  private breadcrumbPath: BehaviorSubject<any> = new BehaviorSubject([]);
  private layer: string;
  private analyzeRid: string;
  private overviewPageContentsList: any[] = [];
  private attributeFilters: any[] = [];
  private isGridDrillingMode = false;
  private pinGridSchema: any;
  private appSwitchActive: boolean;
  private selectedAnalyzeView: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private _tariffSchema: BehaviorSubject<any> = new BehaviorSubject(undefined);
  tariffSchema$ = this._tariffSchema.asObservable();

  constructor(
    private makersiteService: MakersiteService,
    private eventDelegatorService: EventDelegatorService,
    public dialog: MatDialog,
    private toastrService: ToastrService,
    private localStorageService: LocalStorageService,
    private globalDataService: GlobalDataService,
    private router: Router
  ) {}

  setAnalyzeData(data, isDrill = false, isFromGrid = false) {
    const path = this.breadcrumbPath.value;
    if (isDrill && isFromGrid) {
      // drill in from grid
      this.eventDelegatorService.emiter('custom-ngx-event', {
        type: 'drill-in-grid',
        action: 'drill',
        data,
        node: path[path.length - 1],
        controller: window.location.pathname
      });
    } else {
      this.analyzeData.next(data || undefined);
    }

    this.setPinGridSchema(data?.pin_grid_schema);

    if (!data) return;
    if (data.nodes?.length === 0) {
      this.showWarningDialog();
    }
    if (path.length === 0) {
      let findRootNode = data.nodes.find((e) => e.rid.replace('#', '') === this.analyzeRid);
      if (!findRootNode && this.analyzeData.value.save_will_override)
        findRootNode = data.nodes.find((e) => e.route.replace('#', '') === this.analyzeRid);
      this.updateBreadcrumbPath(findRootNode);
    }
    if (data?.pins) this.validatePinsFromBackend();
  }
  getAnalyzeData(): Observable<any> {
    return this.analyzeData.asObservable();
  }

  setGridDrillingMode(drill: boolean) {
    // when this mode is true(api grid=true), then switch view need to call api again with grid=false(otherwise sneaky throws error)
    this.isGridDrillingMode = drill;
  }
  getGridDrillingMode() {
    return this.isGridDrillingMode;
  }

  /** *
   * @param node - this is for updating breadcrumb, if we pass array then it will be reset all breadcrumb values, if we pass on node then it push value in existing array
   */
  updateBreadcrumbPath(node) {
    let currentValue = this.breadcrumbPath.value;
    if (Array.isArray(node) && !node.length) {
      this.breadcrumbPath.next([]);
      return;
    }
    if (Array.isArray(node)) {
      const index = currentValue.findIndex((e) => e.Tier === node[0]?.Tier);
      if (currentValue[index]?.Tier === node[0]?.Tier) {
        currentValue = currentValue.toSpliced(index);
      }
      const updatedValue = [...currentValue, ...node];
      this.breadcrumbPath.next(updatedValue);
      return;
    }
    const updatedValue = [...currentValue, node];
    this.breadcrumbPath.next(updatedValue);
  }

  getBreadcrumbPath(): Observable<any> {
    return this.breadcrumbPath.asObservable();
  }

  setLayer(layer) {
    this.layer = layer;
  }
  getLayer() {
    return this.layer;
  }

  setAnalyzeRid(id) {
    this.analyzeRid = id;
    this.globalDataService.setPpmId(this.analyzeRid);
  }
  getAnalyzeRid() {
    return this.analyzeRid;
  }

  setOverviewPageContentsList(list) {
    this.overviewPageContentsList = list;
  }
  getOverviewPageContentsList(): any[] {
    return this.overviewPageContentsList;
  }

  //* ************ pins setting methods - start ****************/
  setPins(pin: any, isFromRslMenu?: boolean) {
    if (Array.isArray(pin)) {
      this.resetPins(pin);
      return;
    }
    const pins = _.cloneDeep(this.pins.value);
    if (pin.hidden) {
      return;
    }

    if (isFromRslMenu) {
      pins.forEach((e) => {
        // only one rsl pin apply at a time from rsl pin selection menu
        if (e.type === 'display-RSL' && pin.pinid !== e.pinid) e.checkbox = false;

        pin.checkbox = true;
      });
    } else if (!pin.hasOwnProperty('checkbox')) {

      pin.checkbox = true;
    }

    const findIndexOfPin = pins.findIndex((e) => e.pinid === pin.pinid);
    if (findIndexOfPin === -1) {
      pins.push(pin);
    } else {
      pins[findIndexOfPin] = pin;
    }
    this.pins.next(pins);
    if (isFromRslMenu) {
      // if rsl pin selection changed
      this.refreshAnalyzeData();
    }
  }

  removePins(pin, isFromRslMenu?: boolean) {
    const pins = this.pins.value;
    const findIndexOfPin = pins.findIndex((e) => e.pinid === pin.pinid);
    pins.splice(findIndexOfPin, 1);
    this.pins.next(pins);
    if (isFromRslMenu && pin.checkbox) {
      // if removed pin is active then reload analyze data
      this.refreshAnalyzeData();
    }
  }

  getPins() {
    return this.pins.value;
  }

  getPinsAsObservable(): Observable<any> {
    return this.pins.asObservable();
  }

  setAttributeFilters(filter) {
    this.attributeFilters = filter;
  }

  getAttributeFilters() {
    return this.attributeFilters;
  }

  setPinGridSchema(schema) {
    this.pinGridSchema = schema;
  }
  getPinGridSchema() {
    return this.pinGridSchema;
  }

  setTariffSchema(schema) {
    this._tariffSchema.next(schema);
  }

  setAppSwitchStatus(value: boolean) {
    this.appSwitchActive = value;
  }

  getAppSwitchStatus() {
    return this.appSwitchActive;
  }

  getSelectedAnalyzeView() {
    return this.selectedAnalyzeView.asObservable();
  }

  set analyzeView(value: string) {
    this.selectedAnalyzeView.next(value);
  }

  /**
   * this method check and validate pins with BE responded
   * e.g FE send 3 pins but BE return 2 pins then this one extra pin removed from applied pins(i.e extra pin is available in list but checkbox is false).
   * only those pins are active who present in BE response.
   */
  private validatePinsFromBackend() {
    const ids = [];
    const PinsFromResponse = _.cloneDeep(
      this.analyzeData.value.pins.filter((e) => !e.hidden && e.type !== 'attribute-pin-base')
    );
    PinsFromResponse.forEach((resPin) => {
      ids.push(resPin.pinid);
      resPin.replacementRid = this.pins.value.find((pin: any) => pin.pinid === resPin.pinid)?.replacementRid;
      resPin.checkbox = true;
    });
    const pinsFromLocal = _.cloneDeep(this.pins.value.filter((e) => !ids.includes(e.pinid)));
    pinsFromLocal.forEach((e) => {
      e.checkbox = false;
    });
    // pins validations from BE
    this.setPins([...PinsFromResponse, ...pinsFromLocal]);

    // set attribute pins from BE this is only for company layer
    if (this.layer === 'company') {
      const attrPins = _.cloneDeep(
        this.analyzeData.value.pins.filter((e) => !e.hidden && e.type === 'attribute-pin-base')
      );
      const newTempPins = [];
      attrPins.forEach((element) => {
        const attr = this.attributeFilters.find((e) => e.attribute === element.attribute);
        if (attr) {
          attr.pinid = element.pinid;
          attr.version = element.version;
          newTempPins.push(attr);
        }
      });
      this.setAttributeFilters(newTempPins);
    }
  }

  private resetPins(pin: any[]) {
    pin.forEach((e: Record<string, any>) => {
      if (!e.hasOwnProperty('checkbox')) {
        e.checkbox = true;
      }
    });
    const uniquePins = this.getUniquePins(pin);
    this.pins.next(_.cloneDeep(uniquePins));
  }

  getUniquePins(pins: any[], key?: string): any[] {
    const uni = [];
    const ids = [];
    const pinsKey = key || 'pinid';
    const newPins = pins.filter((e) => !e.hidden);
    newPins.forEach((e: any) => {
      if (!ids.includes(e[pinsKey])) {
        ids.push(e[pinsKey]);
        uni.push(e);
      }
    });
    return uni;
  }
  //* ************ pins setting methods - ends ****************/

  refreshAnalyzeData() {
    this.eventDelegatorService.emiter('custom-ngx-event', {
      type: 'toolbar-reload-event',
      controller: window.location.pathname
    });
  }

  exportSubstances() {
    const path = this.breadcrumbPath.value;
    const lastNode = _.cloneDeep(path[path.length - 1]);
    const rid = lastNode.rid || this.analyzeRid;
    this.makersiteService.getExportSubstances(rid, this.layer, this.layer === 'chemical_grading').subscribe((res) => {
      this.downloadFile(res, 'application/xlsx', lastNode.name);
    });
  }

  downloadFile(blob: any, type: string, filename: string): string {
    const url = window.URL.createObjectURL(blob); // <-- work with blob directly
    // create hidden dom element (so it works in all browsers)
    const a = document.createElement('a');
    a.setAttribute('style', 'display:none;');
    document.body.appendChild(a);

    // create file, attach to hidden element and open hidden element
    a.href = url;
    a.download = `${filename.replace(' ', '_')}.xlsx`;
    a.click();
    return url;
  }

  saveState(view) {
    if (this.analyzeData.value.save_will_override) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          header: 'Overwrite Product',
          text: 'Saving the current pinset will overwrite the existing pinset at the current product. Are you sure?',
          cancel_text: 'Cancel',
          confirm_text: 'Ok',
          type: 'Confirm'
        }
      });
      dialogRef.afterClosed().subscribe((confirm) => {
        if (confirm) {
          this.saveDataset(view);
        }
      });
    } else {
      this.saveDataset(view);
    }
  }

  unauthorizedDataset(error, rid?: string, isFromGrid = false) {
    if (rid) {
      const path = _.cloneDeep(this.breadcrumbPath.value);
      if (path[path.length - 1]?.rid.replace('#', '') === rid) path.pop();
      this.updateBreadcrumbPath(path);
    }
    if (error.status === 401) {
      this.dialog.open(UnauthorizedErrorDialog, {
        data: {
          layer: this.layer,
          rid: this.analyzeRid,
          isFromGrid,
          ...error.error
        },
        disableClose: true,
        panelClass: 'unauthorized-error-dialog'
      });
    }
  }

  private showWarningDialog() {
    const path = _.cloneDeep(this.breadcrumbPath.value);
    this.updateBreadcrumbPath(path);
    const data = {
      header: 'Attention',
      type: 'loadGraph',
      text: "We don't have any details about this object.We are constantly adding more information to makersite and would love your help with making it better.If you have more information, please do let us know.",
      cancel_text: 'Dismiss',
    };
    this.dialog.open(ConfirmationDialogComponent, { data });
  }

  invite() {
    this.dialog.open(InviteShareComponent, {
      data: { type: 'showInvite', value: 'Invite' },
      panelClass: 'show-invite'
    });
  }

  share() {
    const userInfo = this.localStorageService.getItem('user_info');
    const link = `/mattermaps/${this.layer}/${this.analyzeRid}`;
    this.dialog.open(InviteShareComponent, {
      data: {
        type: 'showInvite',
        value: 'Share',
        data: {
          fr: userInfo.email,
          link,
          rid: this.analyzeRid,
          usersLackingAccessList: ''
        }
      },
      panelClass: 'show-invite'
    });
  }

  exportPng() {
    this.eventDelegatorService.emiter('custom-ngx-event', {
      type: 'mega-action-event',
      action: 'print-map',
      data: this.breadcrumbPath.value[this.breadcrumbPath.value.length - 1],
      controller: 'app.analyze'
    });
  }

  private saveDataset(selectedView) {
    const path = this.breadcrumbPath.value[0];
    const payload: Record<string, any> = {
      name: path.name,
      resource_rid: this.analyzeRid,
      pins: this.getPins(),
      date: new Date().toUTCString()
    };
    if (this.layer === 'costing' && selectedView === 'costing-grid') {
      payload.type = 'Costmodel';
      payload.data = {
        layer: this.layer
      };
    } else if (selectedView === 'sankey') {
      payload.data = {
        position: [],
        layer: this.layer
      };
      this.eventDelegatorService.emiter('custom-ngx-event', {
        type: 'mega-action-event',
        action: 'save-sankey-action',
        data: payload,
        controller: 'app.analyze'
      });
      return;
    } else if (selectedView === 'graph') {
      payload.data = {
        position: [],
        layer: this.layer
      };
      payload.type = 'network-graph';
    }

    const schema = {
      header: 'Save as product',
      lines: [],
      formSchema: [
        {
          defaultValue: null,
          description: 'Name',
          fieldtype: 'STRING',
          formtype: 'text',
          key: 'name',
          sortlevel: 10,
          title: 'Name',
          mandatory: true
        },
        {
          defaultValue: null,
          description: 'Description',
          fieldtype: 'STRING',
          formtype: 'textarea',
          key: 'description',
          sortlevel: 20,
          title: 'Description'
        }
      ]
    };

    const dialogRef = this.dialog.open(InlineFormSchemaDialogComponent, {
      panelClass: 'inline-schema',
      data: schema
    });

    const instance = dialogRef.componentInstance;

    instance.submitDataEmitter.subscribe((data) => {
      if (data && data.value.Inputs) {
        Object.assign(payload, data.value.Inputs);
        this.makersiteService.saveSankey(payload).subscribe((res: any) => {
          this.toastrService.showMessage('Saved Successfully', 2000);
          dialogRef?.close();
          this.router.navigate(['mattermaps/overview/', res.rid]);
        });
      }
    });
  }

  /**
   * this method return height and width of type(i.e is a container id)
   * @param type is 'graph' or 'sankey'
   */
  getViewportSize(type, elementRef?: any) {
    const margin = { top: 1, right: 1, bottom: 6, left: 1 };
    const mainContainer = elementRef ?? document.getElementById(type);
    const windowHeight = mainContainer?.clientHeight;
    const windowWidth = mainContainer?.clientWidth;
    let height = 0;
    let width = 0;
    if (windowHeight && windowWidth) {
      width = windowWidth - margin.left - margin.right;
      height = windowHeight - margin.top - margin.bottom;
    }

    return { height, width };
  }
}
